import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdArrowBack, MdCheckCircle, MdSave } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { allChannels } from 'variables/industries';

interface Seller {
    _id: string;
    name: string;
    surname: string;
    email: string;
    company?: string;
    createdAt: string;
    channels?: string[];
}

export default function BackOfficeSellersChannels() {
    const navigate = useNavigate();
    const [sellers, setSellers] = useState<Seller[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Record<string, string[]>>({});
    const [saving, setSaving] = useState<Record<string, boolean>>({});

    useEffect(() => {
        fetchSellers();
    }, []);

    const fetchSellers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/sellers/missing-channels`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSellers(response.data.sellers);
        } catch (error) {
            console.error('Error fetching sellers without channels:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleChannel = (sellerId: string, channel: string) => {
        setSelected((prev) => {
            const current = prev[sellerId] || [];
            const next = current.includes(channel)
                ? current.filter((c) => c !== channel)
                : [...current, channel];
            return { ...prev, [sellerId]: next };
        });
    };

    const saveChannels = async (sellerId: string) => {
        const channels = selected[sellerId] || [];
        if (channels.length === 0) return;

        try {
            setSaving((prev) => ({ ...prev, [sellerId]: true }));
            const token = localStorage.getItem('token');
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${sellerId}`, { channels }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSellers((prev) => prev.filter((s) => s._id !== sellerId));
        } catch (error) {
            console.error('Error saving channels:', error);
        } finally {
            setSaving((prev) => ({ ...prev, [sellerId]: false }));
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/back-office/users')}
                            className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                        >
                            <MdArrowBack className="text-xl" />
                        </button>
                        <div>
                            <div className="text-2xl font-bold text-navy-700 dark:text-white">
                                Sellers Channels
                            </div>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Sellers without any acquisition channel assigned. Pick channels for each and save.
                            </p>
                        </div>
                    </div>
                    {!loading && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            {sellers.length} to do
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                ) : sellers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                        <MdCheckCircle className="mb-3 text-5xl text-green-500" />
                        All sellers have channels assigned.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {sellers.map((seller) => (
                            <div
                                key={seller._id}
                                className="rounded-lg border border-gray-200 p-4 dark:border-navy-600"
                            >
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                            {seller.name} {seller.surname}
                                            {seller.company ? ` — ${seller.company}` : ''}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {seller.email} · Inscrit le {new Date(seller.createdAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => saveChannels(seller._id)}
                                        disabled={(selected[seller._id]?.length || 0) === 0 || saving[seller._id]}
                                        className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <MdSave />
                                        {saving[seller._id] ? 'Saving...' : 'Save'}
                                    </button>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {allChannels.map((channel) => {
                                        const isSelected = (selected[seller._id] || []).includes(channel);
                                        return (
                                            <button
                                                key={channel}
                                                type="button"
                                                onClick={() => toggleChannel(seller._id, channel)}
                                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                                    isSelected
                                                        ? 'bg-brand-500 text-white'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                                }`}
                                            >
                                                {channel}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
